import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { getWeeksInMonth, startOfMonth, format, getDate } from 'date-fns'
import { DefaultRootState, useDispatch, useSelector } from 'react-redux'
import { Link, animateScroll as scroll } from 'react-scroll'
import Lottie from 'react-lottie'
import ptBR from 'date-fns/locale/pt-BR'
import {
  CircularProgress,
  Modal,
  FormControl,
  Select,
  InputLabel,
  MenuItem
} from '@material-ui/core'
import {
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Tooltip as MuiTooltip
} from '@mui/material'
import {
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
  LockOpen as LockOpenIcon,
  LockOutlined as LockIcon
} from '@material-ui/icons'

import { ICurrentGoalProps } from '../..'
import { Content } from '../../styles'
import PromptModal from 'components/GoalWizard/PromptModal'
import { Calendar } from '../Calendar'
import {
  setInitialCalendarValues
} from 'store/modules/calendarGoal/actions'
import palette from 'theme/palette'
import Tooltip from 'components/Tooltip'
import { IDays, ISection, IUserWorking } from 'store/modules/goal/types'
import { distributeGoalValues } from '../distributeGoalValues'
import { getDay } from 'date-fns/esm'
import { ICalendarGoalProps } from 'store/modules/calendarGoal/types'
import { toast } from 'react-toastify'
import api from 'repositories/api'

import wandAnimation from 'assets/wandAnim.json'
import WandIcon from 'assets/Icons/WandIcon'

import {
  CreateIcon,
  EditButton,
  NextStepButton
} from 'views/Goals/components/AddGoal/style'

import {
  CalendarContainer,
  Footer,
  LoadingCalendar,
  LockButton,
  OpenSectionsButton,
  SuggestionAnimation,
  SuggestionButton,
  SuggestionContainer,
  TitleContainer,
  WarningTurnOffSuggestionText
} from './styles'

interface SelectorCalendarGoal {
  state: DefaultRootState
  calendarGoal: ICalendarGoalProps
}

interface IGoalDaysWeightSuggestion {
  days: number[]
  metadata: {
    fromStore: boolean
    soldExpected: number
    amountOfInputsConsidered: number
    month: string
  }
}

export interface IDwt {
  days: number[]
  weeks: number[]
  total: number
}

interface CalendarSectionProps {
  goal: ICurrentGoalProps
  days: IDays[]
  goalId: string
  defaultDays: IDays[]
  usersWorking: IUserWorking[]
  suggestionAnimationModal: boolean
  isLoadingSuggestionRequest: boolean
  basicCommissionView: boolean
  sectionsOpened: boolean
  loadingUpload: boolean
  sections: ISection[]

  updateGoal: (goal: Partial<ICurrentGoalProps>) => void
  updateCurrentGoal: (goal: Partial<ICurrentGoalProps>) => void
  setDeleteStep: (steps: string[]) => void

  setDays: Dispatch<SetStateAction<IDays[]>>
  setSections: Dispatch<SetStateAction<ISection[]>>
  setIsLoadingSuggestionRequest: Dispatch<SetStateAction<boolean>>
  setSuggestionAnimationModal: Dispatch<SetStateAction<boolean>>
  setIsCreatingSectionModalOpen: Dispatch<SetStateAction<boolean>>
}

export const CalendarSection = ({
  goal,
  days,
  goalId,
  defaultDays,
  usersWorking,
  suggestionAnimationModal,
  isLoadingSuggestionRequest,
  basicCommissionView,
  sectionsOpened,
  sections,
  loadingUpload,
  updateGoal,
  updateCurrentGoal,
  setDays,
  setSections,
  setIsLoadingSuggestionRequest,
  setSuggestionAnimationModal,
  setIsCreatingSectionModalOpen,
  setDeleteStep
}: CalendarSectionProps) => {
  const [suggestionModal, setSuggestionModal] = useState(false)
  const [totalDaysValue, setTotalDaysValue] = useState(0)
  const [isCalendarLocked, setIsCalendarLocked] = useState(true)
  const [lastYearSuggestion, setLastYearSuggestion] =
    useState<IGoalDaysWeightSuggestion | null>(null)
  const [isLoadingLastYearSuggestion, setIsLoadingLastYearSuggestion] =
    useState(false)

  const modelsList = [
    {
      model: 'arima',
      name: 'INTELIGÊNCIA ARTIFICIAL'
    },
    { model: 'lastYear', name: 'ANO ANTERIOR' }
  ]
  const [currentModel, setCurrentModel] = useState(modelsList[0])
  // DWT => Day, Week, Total
  const [dwt, setDwt] = useState<IDwt>({
    days: new Array(7).fill(0),
    weeks: new Array(getWeeksInMonth(new Date(goal?.month))).fill(0),
    total: 0
  })

  const { days: calendarGoal } = useSelector<
    SelectorCalendarGoal,
    ICalendarGoalProps
  >(state => state.calendarGoal)

  const dispatch = useDispatch()

  const animationOptions = {
    // Animação da varinha de sugestão NeoPro
    loop: true,
    autoplay: true,
    animationData: wandAnimation
  }

  const getSuggestionWeights =
    async (): Promise<IGoalDaysWeightSuggestion | null> => {
      try {
        setIsLoadingSuggestionRequest(true)
        const { data } = await api.axios.get<IGoalDaysWeightSuggestion>(
          `/goal/${goalId}/suggestion?model=${currentModel.model}`
        )
        if (data) {
          return data
        }
        return null
      } catch (error) {
        console.error('Erro ao buscar pesos de sugestão:', error)
        return null
      } finally {
        setIsLoadingSuggestionRequest(false)
      }
    }

  const distributeGoalByWeights = (
    targetDays: IDays[],
    weights: number[],
    total: number
  ) => {
    const workingIndices = targetDays
      .map((day, i) => (day.working ? i : -1))
      .filter(i => i !== -1)

    const distribution = distributeGoalValues(total, workingIndices, weights)

    return targetDays.map((day, i) => {
      if (!day.working) return { ...day, goal: 0 }
      if (distribution.has(i)) return { ...day, goal: distribution.get(i)! }
      return { ...day, goal: 0 }
    })
  }

  const handleSuggestion = async (
    suggestionData?: IGoalDaysWeightSuggestion
  ) => {
    try {
      const currentSuggestionData =
        suggestionData ?? (await getSuggestionWeights())

      if (currentSuggestionData && currentSuggestionData.days) {
        const weights = currentSuggestionData.days

        if (weights.length === 0) {
          throw new Error('Array de pesos vazio recebido.')
        }

        const total = goal?.mainGoals.total
        const newDays = distributeGoalByWeights(days, weights, total)

        dispatch(setInitialCalendarValues(newDays))
        setDays(newDays)
        setDwt(generateDwt(newDays))
        updateGoal({
          lastDaysBackup: newDays,
          daysWeightSuggestion: weights,
          config: { ...goal?.config, suggestion: true }
        })
      }
    } catch (error) {
      if (error)
        toast.error('Não foi possível gerar uma sugestão para esse mês')
    } finally {
      setSuggestionAnimationModal(false)
    }
  }

  const hasUsersWorking = goal?.usersWorking.length > 0
  const hasMonthGoals = goal?.monthGoals.length > 0
  const hasFixedCommission = goal?.config?.commission?.fixed?.commission >= 0
  const hasCalendarDays = goal?.days.length > 0
  const hasSuggestion = goal?.config.suggestion
  const hasDaysWeightSuggestion = goal?.daysWeightSuggestion.length > 0
  const hasGoalDays = goal?.days.length > 0
  const hasDays = days.length > 0

  useEffect(() => {
    const everyDayHasTheSameGoal = days.every(day => day.goal === days[0].goal)
    const everyLastDaysBackupHasTheSameGoal = goal?.lastDaysBackup?.every(
      day => day.goal === days[0]?.goal
    )

    if (
      hasSuggestion &&
      hasDaysWeightSuggestion &&
      hasDays &&
      everyDayHasTheSameGoal &&
      everyLastDaysBackupHasTheSameGoal
    ) {
      const notEveryDaysWeightSuggestionAreTheSame =
        !goal?.daysWeightSuggestion?.every(
          weightSuggestion => weightSuggestion === goal?.daysWeightSuggestion[0]
        )

      if (notEveryDaysWeightSuggestionAreTheSame) {
        const total = goal?.mainGoals.total
        const weights = goal?.daysWeightSuggestion

        const newDays = distributeGoalByWeights(days, weights, total)

        dispatch(setInitialCalendarValues(newDays))
        setDays(newDays)
        setDwt(generateDwt(newDays))
        updateGoal({ lastDaysBackup: newDays })
      }
    }
  }, [
    goal?.daysWeightSuggestion,
    days,
    goal?.lastDaysBackup,
    goal?.config.suggestion
  ])

  const calcDaysWeightSuggestion = (days: IDays[]) => {
    return days.map(day => day.goal / goal.mainGoals.total)
  }

  // DEFINE OS DIAS QUE SERÃO RENDERIZADOS NO CALENDÁRIO
  useEffect(() => {
    const hasGoalMonth = !!goal?.month
    const hasGoalUsersWorking = goal?.usersWorking.length > 0
    if (hasGoalMonth && hasGoalUsersWorking) {
      const inCreatingGoal = goal?.days.length === 0

      const newDays = inCreatingGoal ? defaultDays : goal?.days

      // 1. Detect non-working days
      const disabledIndices = newDays
        .map((day: IDays, i: number) => (!day.working ? i : -1))
        .filter((i: number) => i !== -1)

      // 2. Only compute weights if AI weights don't exist yet
      const hasExistingWeights = goal?.daysWeightSuggestion?.length > 0
      const weightsToUse = hasExistingWeights 
        ? goal.daysWeightSuggestion 
        : calcDaysWeightSuggestion(newDays)

      // 3. IA ON with weights: Apply distribution using total weight normalization
      const workingDays = newDays.filter((day: IDays) => day.working)
      const hasWorkingDays = workingDays.length > 0
      const hasDisabledDays = disabledIndices.length > 0

      if (goal?.config?.suggestion && hasWorkingDays) {
        const weights = weightsToUse
        const total = goal?.mainGoals?.total || 0

        // Preservar dias editados manualmente (goalLocked) pelo usuário
        const lockedIndices = newDays
          .map((day: IDays, i: number) => (day.goalLocked ? i : -1))
          .filter((i: number) => i !== -1)
        const hasLockedDays = lockedIndices.length > 0

        let redistributedDays: IDays[]

        if (hasLockedDays) {
          const fixedGoalSum = lockedIndices.reduce(
            (sum: number, idx: number) => sum + newDays[idx].goal, 0
          )
          const workingUnlockedIndices = newDays
            .map((day: IDays, i: number) =>
              day.working && !day.goalLocked ? i : -1
            )
            .filter((i: number) => i !== -1)

          if (workingUnlockedIndices.length > 0) {
            const distribution = distributeGoalValues(
              Math.max(0, total - fixedGoalSum),
              workingUnlockedIndices,
              weights
            )

            redistributedDays = newDays.map((day: IDays, i: number) => {
              if (day.goalLocked) return day
              if (!day.working) return { ...day, goal: 0 }
              if (distribution.has(i)) return { ...day, goal: distribution.get(i)! }
              return { ...day, goal: 0 }
            })
          } else {
            redistributedDays = newDays
          }
        } else {
          redistributedDays = distributeGoalByWeights(newDays, weights, total)
        }

        dispatch(setInitialCalendarValues(redistributedDays))
        setDwt(generateDwt(redistributedDays))
        updateGoal({
          lastDaysBackup: redistributedDays,
          daysWeightSuggestion: weights
        })
        setDays(redistributedDays)
        return
      }

      // 4. IA OFF: Equal redistribution
      let redistributedDays = newDays
      if (hasDisabledDays && hasWorkingDays) {
        const mainGoalValue = goal?.mainGoals.total ?? newDays.reduce(
          (acc: number, day: IDays) => acc + day.goal, 0
        )

        const lockedIndices = newDays
          .map((day: IDays, i: number) => (day.goalLocked ? i : -1))
          .filter((i: number) => i !== -1)
        const hasLockedDays = lockedIndices.length > 0

        let distribution: Map<number, number> | null = null

        if (hasLockedDays) {
          const fixedGoalSum = lockedIndices.reduce(
            (sum: number, idx: number) => sum + newDays[idx].goal, 0
          )
          const workingUnlockedIndices = newDays
            .map((day: IDays, i: number) => (day.working && !day.goalLocked ? i : -1))
            .filter((i: number) => i !== -1)

          if (workingUnlockedIndices.length > 0) {
            distribution = distributeGoalValues(Math.max(0, mainGoalValue - fixedGoalSum), workingUnlockedIndices)
          }
        } else {
          const workingIndices = newDays
            .map((day: IDays, i: number) => (day.working ? i : -1))
            .filter((i: number) => i !== -1)
          distribution = distributeGoalValues(mainGoalValue, workingIndices)
        }

        if (distribution) {
          redistributedDays = newDays.map((day: IDays, i: number) => {
            if (day.goalLocked) return day
            if (!day.working) return { ...day, goal: 0 }
            if (distribution!.has(i)) return { ...day, goal: distribution!.get(i)! }
            return { ...day, goal: 0 }
          })
        }
      }

      dispatch(setInitialCalendarValues(redistributedDays))
      setDwt(generateDwt(redistributedDays))
      updateGoal({
        lastDaysBackup: redistributedDays,
        daysWeightSuggestion: weightsToUse
      })
      setDays(redistributedDays)
    }
  }, [goal?.month, goal?.usersWorking, defaultDays])

  const generateDwt = (days: IDays[]) =>
    days.reduce(
      (acc, day) => {
        if (!day.working) return acc

        const weekStartIndex = getDay(startOfMonth(new Date(goal?.month)))
        const dayIndex = weekStartIndex + (getDate(new Date(day.date)) - 1)
        const weekIndex = Math.floor(dayIndex / 7)

        acc.weeks[weekIndex] += day.goal
        acc.days[dayIndex - weekIndex * 7] += day.goal
        acc.total += day.goal

        return acc
      },
      {
        days: new Array(7).fill(0) as number[],
        weeks: new Array(getWeeksInMonth(new Date(goal?.month))).fill(
          0
        ) as number[],
        total: 0
      }
    )

  const differenceBetweenMonths =
    new Date(goal?.month).getFullYear() > new Date().getFullYear()
      ? -1
      : new Date().getMonth() - new Date(goal?.month).getMonth()

  const handleOffSuggestion = (newDays?: IDays[]) => {
    const sourceDays = newDays ? newDays : days
    const workingIndices = sourceDays
      .map((day, i) => (day.working ? i : -1))
      .filter(i => i !== -1)

    const total = goal?.mainGoals.total
    const distribution = distributeGoalValues(total, workingIndices)

    const redistributedDays = [...sourceDays].map((day, i) => {
      const { goalLocked, ...dayWithoutLock } = day
      if (!day.working) return { ...dayWithoutLock, goal: 0 }
      if (distribution.has(i)) return { ...dayWithoutLock, goal: distribution.get(i)! }
      return { ...dayWithoutLock, goal: 0 }
    })

    setDays(redistributedDays)
    setDwt(generateDwt(redistributedDays))
    updateGoal({
      lastDaysBackup: redistributedDays,
      config: { ...goal?.config, suggestion: false }
    })
  }

  const handleOpenSections = () => {
    setIsCreatingSectionModalOpen(true)
    if (goal?.sections) {
      setSections(goal.sections)
    }
  }

  const handleToggleLockedCalendar = () => {
    if (!isCalendarLocked) {
      const hasNoCalendarGoal = !(calendarGoal.length > 0)
      if (hasNoCalendarGoal) {
        goal?.config.suggestion ? handleSuggestion() : handleOffSuggestion()
      } else {
        setDwt(generateDwt(calendarGoal))
        setDays([...calendarGoal])
      }
    }
    setIsCalendarLocked(prev => !prev)
  }

  const handleToggleNeoProSuggestion = () => {
    if (goal?.config.suggestion) {
      handleOffSuggestion()
    } else {
      setSuggestionModal(prev => !prev)
    }
  }

  const handleSubmitCalendar = () => {
    const newSections = goal.sections ? goal.sections : sections

    updateCurrentGoal({
      lastDaysBackup: days,
      days: days,
      sections: newSections
    })
    scroll.scrollToBottom()
  }

  const handleTurnOnSuggestion = () => {
    setSuggestionModal(false)
    setSuggestionAnimationModal(true)

    if (currentModel.model === 'lastYear' && lastYearSuggestion) {
      handleSuggestion(lastYearSuggestion)
    } else {
      handleSuggestion()
    }
  }

  const canShowCalendarSection =
    (hasUsersWorking && hasMonthGoals && hasFixedCommission) || hasCalendarDays

  const isAFutureMonth = differenceBetweenMonths > -1

  const hasFilledDaysAndSections =
    goal?.days?.length > 0 && goal?.sections.length > 0

  const monthLongFormat = goal?.month
    ? format(new Date(goal?.month), 'MMMM', {
        locale: ptBR
      })
    : '...'

  const daysGoalSumIsDifferentFromStoreGoal =
    Number(totalDaysValue).toFixed(2) !==
    Number(goal?.mainGoals?.total).toFixed(2)

  const isEditingSection = goal?.sections.length >= 2 && !sectionsOpened

  const isNextStepButtonDisabled =
    daysGoalSumIsDifferentFromStoreGoal || isEditingSection

  // Efeito para buscar a sugestão do ano anterior quando o modal abre
  useEffect(() => {
    const fetchLastYearSuggestion = async () => {
      if (!goalId || !suggestionModal) return

      setIsLoadingLastYearSuggestion(true)
      setLastYearSuggestion(null)
      try {
        const { data } = await api.axios.get<IGoalDaysWeightSuggestion>(
          `/goal/${goalId}/suggestion?model=lastYear`
        )
        if (data.metadata.amountOfInputsConsidered <= 0)
          setCurrentModel({
            model: 'arima',
            name: 'INTELIGÊNCIA ARTIFICIAL'
          })
        setLastYearSuggestion(data)
      } catch (error) {
        console.error('Erro ao buscar sugestão do ano anterior:', error)
        setLastYearSuggestion(null)
      } finally {
        setIsLoadingLastYearSuggestion(false)
      }
    }

    if (suggestionModal) {
      fetchLastYearSuggestion()
    }
  }, [suggestionModal, goalId])

  return (
    <>
      {canShowCalendarSection && (
        <Content isFilled={goal?.days?.length > 0 && goal?.sections.length > 0}>
          {(isLoadingSuggestionRequest && !suggestionAnimationModal) ||
          loadingUpload ? (
            !basicCommissionView && (
              <LoadingCalendar>
                <CircularProgress size={20} />
                <span>Calculando dados do Calendário...</span>
              </LoadingCalendar>
            )
          ) : (
            <CalendarContainer>
              <TitleContainer>
                <h1>Meta da loja para {monthLongFormat}</h1>

                <SuggestionContainer>
                  {hasSuggestion && isAFutureMonth && (
                    <LockButton
                      onClick={handleToggleLockedCalendar}
                      isCalendarLocked={isCalendarLocked}
                    >
                      {isCalendarLocked ? <LockIcon /> : <LockOpenIcon />}
                    </LockButton>
                  )}

                  <SuggestionButton
                    suggestionActive={goal?.config.suggestion}
                    data-cy='toggleNeoProSuggestionButton'
                    onClick={handleToggleNeoProSuggestion}
                  >
                    <WandIcon color={'#fff'} size={10} />
                    <span>
                      Sugestão NeoPro
                      {hasSuggestion ? ' Ativada' : ' Desativada'}
                    </span>
                  </SuggestionButton>

                  <ErrorOutlineOutlinedIcon
                    fontSize='small'
                    style={{ color: '#9E9E9E', marginLeft: 7 }}
                    data-tip
                    data-for='tooltip-neopro-suggestion'
                  />

                  <Tooltip
                    content={
                      <p>
                        As metas dos dias serão ajustadas para a sua loja pela
                        nossa Inteligência Artificial.
                      </p>
                    }
                    id='tooltip-neopro-suggestion'
                  />

                  <Modal open={suggestionModal}>
                    <PromptModal
                      icon={<WandIcon color={palette.primary.main} size={20} />}
                      onClose={() => setSuggestionModal(false)}
                      title='Aplicar a Sugestão NeoPro?'
                      description='
            Nosso modelo de sugestão analisa sua loja e mercado em vários aspectos.
            A partir desses dados, é calculada uma Sugestão NeoPro de meta do mês.'
                      onLeft={() => setSuggestionModal(false)}
                      onRight={handleTurnOnSuggestion}
                      rightTitle='Fazer a mágica'
                    >
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          marginBottom: 1,
                          color: 'text.secondary'
                        }}
                      >
                        Escolha o modelo de sugestão:
                      </Typography>
                      <ToggleButtonGroup
                        sx={{ marginBottom: 2 }}
                        color='primary'
                        value={currentModel.model}
                        exclusive
                        onChange={(_, newModel) => {
                          if (newModel !== null) {
                            const selectedModel = modelsList.find(
                              m => m.model === newModel
                            )
                            if (selectedModel) {
                              setCurrentModel(selectedModel)
                            }
                          }
                        }}
                        aria-label='Model selection'
                      >
                        {modelsList.map(model => {
                          const isLastYearModel = model.model === 'lastYear'
                          const disableLastYearButton =
                            isLastYearModel &&
                            (isLoadingLastYearSuggestion ||
                              !lastYearSuggestion ||
                              (lastYearSuggestion &&
                                lastYearSuggestion.metadata
                                  .amountOfInputsConsidered <= 0))

                          const showDisabledTooltip =
                            isLastYearModel &&
                            disableLastYearButton &&
                            !isLoadingLastYearSuggestion &&
                            lastYearSuggestion &&
                            lastYearSuggestion.metadata
                              .amountOfInputsConsidered <= 0

                          const button = (
                            <ToggleButton
                              sx={{ fontSize: '0.75rem' }}
                              key={model.model}
                              value={model.model}
                              disabled={disableLastYearButton}
                            >
                              {model.name}
                              {isLoadingLastYearSuggestion &&
                                isLastYearModel && (
                                  <CircularProgress
                                    size={14}
                                    style={{ marginLeft: '8px' }}
                                  />
                                )}
                            </ToggleButton>
                          )

                          if (showDisabledTooltip) {
                            return (
                              <MuiTooltip
                                key={model.model}
                                title={
                                  <p>
                                    Modelo indisponível por falta de dados do
                                    ano anterior.
                                  </p>
                                }
                                placement='top'
                              >
                                <span>{button}</span>
                              </MuiTooltip>
                            )
                          }

                          return button
                        })}
                      </ToggleButtonGroup>
                      <WarningTurnOffSuggestionText>
                        Atenção: os valores já inseridos nos dias serão
                        perdidos!
                      </WarningTurnOffSuggestionText>
                    </PromptModal>
                  </Modal>
                  <Modal open={suggestionAnimationModal}>
                    <SuggestionAnimation>
                      <Lottie
                        options={animationOptions}
                        width={200}
                        height={200}
                      />
                      <span>
                        <strong>Fazendo a mágica...</strong> <br />
                        Calculando sugestão
                      </span>
                    </SuggestionAnimation>
                  </Modal>
                </SuggestionContainer>
              </TitleContainer>

              <Calendar
                goal={goal}
                dwt={dwt}
                setDwt={setDwt}
                days={days}
                setDays={setDays}
                weekStartIndex={getDay(new Date(goal?.month))}
                month={new Date(goal?.month)}
                mainGoalValue={goal?.mainGoals?.total}
                setTotalDaysValue={setTotalDaysValue}
                updateGoal={updateGoal}
                suggestion={goal?.config.suggestion}
                isCalendarLocked={isCalendarLocked}
                usersWorking={usersWorking}
              />

              <Footer>
                {!hasGoalDays && (
                  <>
                    {goal?.sections.length > 1 && (
                      <OpenSectionsButton
                        needReview={!sectionsOpened}
                        isCompleted={
                          sectionsOpened && goal?.sections.length > 0
                        }
                        onClick={handleOpenSections}
                      >
                        <span>{goal?.sections?.length || 0}</span>
                        {goal?.sections?.length === 1 ? 'Período' : 'Períodos'}
                      </OpenSectionsButton>
                    )}

                    <div data-tip data-for={'tooltip-distribute-issue'}>
                      <NextStepButton
                        data-cy='calendarNextButton'
                        disabled={isNextStepButtonDisabled}
                        onClick={handleSubmitCalendar}
                        style={{ margin: 0 }}
                      >
                        Avançar
                      </NextStepButton>
                    </div>

                    {(isEditingSection ||
                      daysGoalSumIsDifferentFromStoreGoal) && (
                      <Tooltip
                        id='tooltip-distribute-issue'
                        content={
                          <p>
                            {daysGoalSumIsDifferentFromStoreGoal
                              ? 'É necessário que a soma das metas diárias resultem exatamente no total definido para a meta da loja!'
                              : !sectionsOpened &&
                                'Você precisa revisar a criação de períodos para prosseguir!'}
                          </p>
                        }
                      />
                    )}
                  </>
                )}
              </Footer>
            </CalendarContainer>
          )}
        </Content>
      )}

      {hasFilledDaysAndSections && !isLoadingSuggestionRequest && (
        <Link to='calendar' offset={-170} smooth={true}>
          <EditButton onClick={() => setDeleteStep(['days'])}>
            Alterar
            <CreateIcon />
          </EditButton>
        </Link>
      )}
    </>
  )
}
